/**
 * The two top-level, non-overlapping buckets that constitute the total
 * encounter volume. Every encounter is either inpatient or outpatient, so the
 * total is their sum. The finer sub-breakdowns (emergency, surgical,
 * radiology, ambulatory) are portions of these buckets — tracked independently
 * as subsets and never added into the total.
 */
export const TOTAL_ENCOUNTER_KEYS = ["inpatient", "outpatient"] as const;

const PRIMARY_KEY_SET = new Set<string>(TOTAL_ENCOUNTER_KEYS);

/**
 * True for the primary buckets (inpatient / outpatient) that partition the
 * total. Everything else is a sub-breakdown (a subset of one primary bucket).
 */
export function isPrimaryEncounterKey(key: string): boolean {
  return PRIMARY_KEY_SET.has(key);
}

export function finiteNonNegativeEncounterVolume(
  value: unknown,
): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : null;
}

/**
 * Compute the total encounter volume as the sum of the primary buckets
 * (inpatient + outpatient). Returns `null` when neither bucket has an explicit
 * value so callers can fall back to other estimation strategies.
 */
export function sumTotalEncounterVolumes(
  encounterVolumes: Partial<Record<string, number>> | undefined,
): number | null {
  if (!encounterVolumes) return null;

  let sawExplicitSetting = false;
  let total = 0;
  for (const key of TOTAL_ENCOUNTER_KEYS) {
    const value = finiteNonNegativeEncounterVolume(encounterVolumes[key]);
    if (value == null) continue;
    sawExplicitSetting = true;
    total += value;
  }

  return sawExplicitSetting ? total : null;
}
